import React, {forwardRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {Divider, Flex, useStyleConfig, Text, Button} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import Ratings from '../ratings'
import RatingsBreakdown from './partials/ratings-breakdown'
import AddReviews from './partials/add-reviews'
import Review from '../../components/review'
import {ReviewsModal} from '../reviews-modal'
import {trackRRClickStars} from '../../tracking/analytics'

const ReviewOverview = forwardRef(
    (
        {
            ratingsBreakdown,
            review,
            reviewsCount,
            totalRating,
            writeReviewLoading,
            onWriteReview,
            productSearchIdList,
            onCloseReviewModal,
            isOpenReviewModal,
            onOpenReviewModal
        },
        ref
    ) => {
        const styles = useStyleConfig('ReviewOverview', {variant: review ? 'default' : 'no-review'})
        const productId = productSearchIdList || review?.ProductId

        const onRatingsClick = useCallback(() => {
            trackRRClickStars('review section')
        }, [])

        return (
            <Flex {...styles.container} ref={ref}>
                <Text as="h2" {...styles.heading}>
                    <FormattedMessage defaultMessage="Review Snapshot" id="reviews.overview" />
                </Text>
                <Divider {...styles.divider} />
                <Flex {...styles.innerContainer}>
                    <Flex {...styles.topContentWrapper}>
                        <Ratings
                            {...styles.ratings}
                            rating={totalRating}
                            count={reviewsCount}
                            variant="reviewsModule"
                            onRatingsClick={onRatingsClick}
                        />
                        {ratingsBreakdown ? (
                            <RatingsBreakdown
                                breakdown={ratingsBreakdown}
                                reviewcount={reviewsCount}
                            />
                        ) : null}
                        {!review ? (
                            <Text {...styles.noReviews}>
                                <FormattedMessage
                                    defaultMessage="Be the first to write a review!"
                                    id="reviews.no_reviews"
                                />
                            </Text>
                        ) : null}
                        <AddReviews
                            reviewCount={reviewsCount}
                            writeReviewLoading={writeReviewLoading}
                            onClick={onWriteReview}
                        />
                    </Flex>
                    {review ? (
                        <Flex {...styles.reviewsContainer}>
                            <Review review={review} />
                        </Flex>
                    ) : null}
                </Flex>

                {reviewsCount > 0 && (
                    <>
                        <Button
                            {...styles.seeAllReviewsBtn}
                            onClick={onOpenReviewModal}
                            data-testid="reviews-see-all"
                        >
                            <FormattedMessage
                                defaultMessage="See all Reviews"
                                id="reviews.see_all_reviews"
                            />
                        </Button>

                        <ReviewsModal
                            isOpen={isOpenReviewModal}
                            onClose={onCloseReviewModal}
                            reviewsCount={reviewsCount}
                            productId={productId}
                        >
                            <Flex direction="column" gap={8} px={{base: 0, lg: 4}}>
                                <Ratings
                                    rating={totalRating}
                                    count={reviewsCount}
                                    {...styles.ratings}
                                    variant="reviewsModule"
                                />

                                {ratingsBreakdown ? (
                                    <RatingsBreakdown
                                        breakdown={ratingsBreakdown}
                                        reviewcount={reviewsCount}
                                    />
                                ) : null}
                            </Flex>
                        </ReviewsModal>
                    </>
                )}
            </Flex>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    ReviewOverview.displayName = 'ReviewOverview'
}

ReviewOverview.propTypes = {
    productSearchIdList: PropTypes.string,
    ratingsBreakdown: PropTypes.array,
    reviewsCount: PropTypes.number,
    totalRating: PropTypes.number,
    writeReviewLoading: PropTypes.bool,
    onWriteReview: PropTypes.func,
    review: PropTypes.object,
    onCloseReviewModal: PropTypes.func,
    isOpenReviewModal: PropTypes.bool,
    onOpenReviewModal: PropTypes.func
}

export default ReviewOverview
